import React, { useState, useEffect, useRef } from 'react';
import "./taxInvoiceInformationEdit.css";
import Button from '../../components/button/Button';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import { useAlert } from '../../components/alert-context/AlertContext';
import { TaxInvoiceInfo, TaxInvoiceInfoRequest } from '../../utils/models';
import { getLocalStorage } from '../../utils/helper';

type Mode = 'list' | 'form';

const emptyForm: TaxInvoiceInfoRequest = {
  taxpayerName: '',
  taxId: '',
  phone: '',
  address: '',
  subDistrict: '',
  district: '',
  province: '',
  postalCode: '',
};

const TaxInvoiceInformationEdit: React.FC = () => {
  const api = useApiServices();
  const alert = useAlert();
  const userId = getLocalStorage('userId') as string;

  const [mode, setMode] = useState<Mode>('list');
  const [list, setList] = useState<TaxInvoiceInfo[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TaxInvoiceInfoRequest>(emptyForm);
  const [saving, setSaving] = useState(false);

  const apiRef = useRef(api);
  apiRef.current = api;

  const fetchList = useRef(async () => {
    try {
      const res = await apiRef.current.GetTaxInvoiceInfosAsync(userId);
      const items: TaxInvoiceInfo[] = res?.result?.items ?? res?.result ?? [];
      setList(Array.isArray(items) ? items : []);
    } catch {
      setList([]);
    }
  });

  useEffect(() => {
    if (userId) fetchList.current();
  }, [userId]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setMode('form');
  };

  const openEdit = (info: TaxInvoiceInfo) => {
    setEditingId(info.id);
    setForm({
      taxpayerName: info.taxpayerName,
      taxId: info.taxId,
      phone: info.phone,
      address: info.address,
      subDistrict: info.subDistrict,
      district: info.district,
      province: info.province,
      postalCode: info.postalCode,
    });
    setMode('form');
  };

  const handleDelete = (info: TaxInvoiceInfo) => {
    alert.confirm({
      message: `ต้องการลบข้อมูล "${info.taxpayerName}" ?`,
      confirmText: 'ลบ',
      cancelText: 'ยกเลิก',
      onConfirm: async () => {
        try {
          await api.DeleteTaxInvoiceInfoAsync(userId, info.id);
          alert.success({ message: 'ลบข้อมูลสำเร็จ' });
          fetchList.current();
        } catch {
          alert.error({ message: 'ไม่สามารถลบข้อมูลได้' });
        }
      },
    });
  };

  const handleSetDefault = async (info: TaxInvoiceInfo) => {
    try {
      await api.SetDefaultTaxInvoiceInfoAsync(userId, info.id);
      alert.success({ message: 'ตั้งค่าเริ่มต้นสำเร็จ' });
      fetchList.current();
    } catch {
      alert.error({ message: 'ไม่สามารถตั้งค่าเริ่มต้นได้' });
    }
  };

  const handleChange = (field: keyof TaxInvoiceInfoRequest) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.taxpayerName || !form.taxId || !form.phone || !form.address ||
        !form.subDistrict || !form.district || !form.province || !form.postalCode) {
      alert.error({ message: 'กรุณากรอกข้อมูลให้ครบถ้วน' });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.UpdateTaxInvoiceInfoAsync(userId, editingId, form);
      } else {
        await api.CreateTaxInvoiceInfoAsync(userId, form);
      }
      alert.success({ message: editingId ? 'แก้ไขข้อมูลสำเร็จ' : 'เพิ่มข้อมูลสำเร็จ' });
      await fetchList.current();
      setMode('list');
    } catch {
      alert.error({ message: 'บันทึกข้อมูลไม่สำเร็จ กรุณาลองใหม่' });
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'form') {
    return (
      <section className="ti-wrapper">
        <div className="ti-container lg:container lg:mx-auto">

          <button className="ti-back-btn" onClick={() => setMode('list')}>
            ← กลับ
          </button>

          <div className="ti-header">
            <h2 className="ti-title">
              {editingId ? 'แก้ไขข้อมูลใบกำกับภาษี' : 'เพิ่มข้อมูลใบกำกับภาษี'}
            </h2>
          </div>

          <div className="ti-section-label">ข้อมูลผู้เสียภาษี</div>
          <div className="ti-card">
            <div className="ti-grid">

              <div className="ti-field">
                <label className="ti-label required">ชื่อผู้เสียภาษี</label>
                <input type="text" placeholder="กรอกชื่อผู้เสียภาษี" className="ti-input" value={form.taxpayerName} onChange={handleChange('taxpayerName')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">เลขประจำตัวผู้เสียภาษี</label>
                <input type="text" placeholder="กรอกเลขประจำตัว" className="ti-input" value={form.taxId} onChange={handleChange('taxId')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">เบอร์ติดต่อ</label>
                <input type="text" placeholder="กรอกเบอร์ติดต่อ" className="ti-input" value={form.phone} onChange={handleChange('phone')} />
              </div>

              <div className="ti-field full">
                <label className="ti-label required">ที่อยู่</label>
                <input type="text" placeholder="กรอกที่อยู่" className="ti-input" value={form.address} onChange={handleChange('address')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">ตำบล</label>
                <input type="text" placeholder="กรอกตำบล" className="ti-input" value={form.subDistrict} onChange={handleChange('subDistrict')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">อำเภอ</label>
                <input type="text" placeholder="กรอกอำเภอ" className="ti-input" value={form.district} onChange={handleChange('district')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">จังหวัด</label>
                <input type="text" placeholder="กรอกจังหวัด" className="ti-input" value={form.province} onChange={handleChange('province')} />
              </div>

              <div className="ti-field">
                <label className="ti-label required">รหัสไปรษณีย์</label>
                <input type="text" placeholder="กรอกรหัสไปรษณีย์" className="ti-input" value={form.postalCode} onChange={handleChange('postalCode')} />
              </div>

            </div>
          </div>

          <div className="ti-actions">
            <Button variant="outline" fullWidth onClick={() => setMode('list')}>ยกเลิก</Button>
            <Button variant="primary" fullWidth onClick={handleSave} disabled={saving}>
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </div>

        </div>
      </section>
    );
  }

  return (
    <section className="ti-wrapper">
      <div className="ti-container lg:container lg:mx-auto">

        <div className="ti-list-header">
          <h2 className="ti-title">ข้อมูลใบกำกับภาษี</h2>
          <button className="ti-add-btn" onClick={openAdd}>+ เพิ่ม</button>
        </div>

        {list.length === 0 ? (
          <div className="ti-empty">
            <span className="ti-empty-text">ยังไม่มีข้อมูลใบกำกับภาษี</span>
            <Button variant="primary" onClick={openAdd}>+ เพิ่มข้อมูล</Button>
          </div>
        ) : (
          list.map(info => (
            <div key={info.id} className="ti-info-card">
              <div className="ti-info-card-top">
                <div className="ti-info-card-body">
                  <div className="ti-info-card-name">{info.taxpayerName}</div>
                  <div className="ti-info-card-detail">เลขภาษี {info.taxId}</div>
                  <div className="ti-info-card-detail">{info.phone}</div>
                  <div className="ti-info-card-detail">
                    {info.address} {info.subDistrict} {info.district} {info.province} {info.postalCode}
                  </div>
                </div>
                {info.isDefault && (
                  <span className="ti-default-badge">ค่าเริ่มต้น</span>
                )}
              </div>
              <div className="ti-card-actions">
                <button className="ti-card-btn edit" onClick={() => openEdit(info)}>แก้ไข</button>
                {!info.isDefault && (
                  <button className="ti-card-btn set-default" onClick={() => handleSetDefault(info)}>
                    ตั้งค่าเริ่มต้น
                  </button>
                )}
                <button className="ti-card-btn delete" onClick={() => handleDelete(info)}>ลบ</button>
              </div>
            </div>
          ))
        )}

      </div>
    </section>
  );
};

export default TaxInvoiceInformationEdit;